"use client";

/**
 * Barra de navegação das etapas do agendamento.
 *
 * Fica no rodapé do card em desktop e fixa na base da viewport em mobile, onde
 * substitui a barra flutuante antiga.
 *
 * É `fixed`, não `sticky`: o card do wizard, o `<main>` e o wrapper da página
 * usam `overflow-hidden`/`overflow-x-hidden`, o que transforma cada um em
 * contexto de rolagem e deixa `position: sticky` inerte.
 */

import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

interface BookingWizardNavProps {
  canAdvance: boolean;
  blockedReason: string | null;
  isFirstStep: boolean;
  /** Etapas opcionais mostram "Pular" no lugar de "Próximo". */
  optionalStep: boolean;
  summaryLabel?: string | null;
  onBack: () => void;
  onNext: () => void;
}

export function BookingWizardNav({
  canAdvance,
  blockedReason,
  isFirstStep,
  optionalStep,
  summaryLabel,
  onBack,
  onNext,
}: BookingWizardNavProps) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-2xl shadow-slate-900/10 backdrop-blur lg:static lg:z-auto lg:px-6 lg:shadow-none">
      <div className="mx-auto max-w-[1280px] lg:max-w-none">
        {blockedReason && (
          <p className="mb-2 text-center text-xs font-medium text-slate-500 sm:text-left">
            {blockedReason}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isFirstStep}
            className="inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold leading-none text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100 disabled:cursor-not-allowed disabled:opacity-40 sm:px-6"
          >
            <FiArrowLeft className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Voltar</span>
          </button>

          {summaryLabel && (
            <p className="hidden min-w-0 flex-1 truncate text-sm font-medium text-slate-600 sm:block">
              {summaryLabel}
            </p>
          )}

          <button
            type="button"
            onClick={onNext}
            disabled={!canAdvance}
            className="inline-flex min-h-[3rem] flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-3 text-sm font-bold leading-none text-white shadow-md shadow-primary-600/20 transition hover:from-primary-hover hover:to-primary-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:shadow-none sm:max-w-[220px] sm:flex-none"
          >
            {optionalStep ? "Pular e continuar" : "Próximo"}
            <FiArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
